<template>
    <NConfigProvider :theme-overrides="themeOverrides">
        <div>
            <Head :title="title" />

            <div>
                <!-- navbar -->
                <nav
                    class="bg-white border-gray-200 border-b px-2 sm:px-4 py-2.5 fixed z-30 w-full"
                >
                    <div
                        class="container flex flex-wrap justify-between items-center mx-auto"
                    >
                        <Link href="/dashboard" class="flex">
                            <img
                                class="mx-2"
                                src="@assets/images/shared/logo-shipro-dark.svg"
                                alt="logo"
                            />
                            <span
                                class="self-center text-xl text-gray-700 font-semibold whitespace-nowrap"
                                >Shipro</span
                            >
                        </Link>
                        <div class="flex items-center md:order-2">
                            <Navbar />
                            <NDropdown
                                trigger="hover"
                                :options="userOptions"
                                placement="bottom-end"
                                size="large"
                                style="font-size: 16px"
                            >
                                <button
                                    type="button"
                                    class="hidden lg:flex mr-3 text-sm bg-white rounded-full md:mr-0"
                                    id="user-menu-button"
                                    aria-expanded="false"
                                    data-dropdown-toggle="dropdown"
                                >
                                    <span class="sr-only">Open user menu</span>
                                    <div
                                        class="w-8 h-8 rounded-full flex items-center"
                                    >
                                        <Icon size="24" class="text-gray-500">
                                            <User />
                                        </Icon>
                                    </div>
                                </button>
                            </NDropdown>
                            <button
                                @click="openDrawer"
                                data-collapse-toggle="mobile-menu-2"
                                type="button"
                                class="inline-flex items-center p-2 ml-1 text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
                                aria-controls="mobile-menu-2"
                                aria-expanded="false"
                            >
                                <span class="sr-only">Open main menu</span>
                                <svg
                                    class="w-6 h-6"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                <svg
                                    class="hidden w-6 h-6"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>
                        <NDrawer v-model:show="drawerActive">
                            <SidebarContent />
                        </NDrawer>
                    </div>
                </nav>

                <div :class="['flex', contrast ? 'bg-gray-50' : 'bg-white']">
                    <div
                        id="main-content"
                        class="min-h-screen h-full w-full relative pt-14"
                    >
                        <main class="lg:px-24 mb-24">
                            <slot></slot>
                        </main>
                        <footer
                            class="text-center absolute right-0 left-0 bottom-0 text-gray-400 py-4"
                        >
                            © 2018-{{ new Date().getFullYear() }} Shipro. All rights reserved.
                        </footer>
                    </div>
                </div>
            </div>
        </div>
    </NConfigProvider>
</template>

<script>
import { defineComponent } from "vue";
import { Head, Link } from "@inertiajs/inertia-vue3";
import NavLink from "@/shared/NavLink.vue";
import { NDrawer, NDrawerContent, NDropdown, NIcon, NDivider } from "naive-ui";
import { h } from "vue";
import { Icon } from "@vicons/utils";
import { NConfigProvider } from "naive-ui";
import SidebarContent from "./SidebarContent.vue";
import Navbar from "./Navbar.vue";

import {
    Truck,
    Key,
    MapPin,
    CalendarCheckRegular,
    LifeRingRegular,
    Building,
    Boxes,
    ChartPie,
    Store,
    User,
    ClipboardList,
    LifeRing,
    SignOutAlt,
    UserCog,
    Plus,
    TrafficLight,
    Buromobelexperte,
} from "@vicons/fa";
import {
    PersonCircleOutline as UserIcon,
    LogOutOutline as LogoutIcon,
} from "@vicons/ionicons5";

export default defineComponent({
    components: {
        Head,
        Link,
        NavLink,
        SidebarContent,
        NConfigProvider,
        NDrawer,
        NDrawerContent,
        NDropdown,
        NIcon,
        NDivider,
        Icon,
        Truck,
        Key,
        MapPin,
        CalendarCheckRegular,
        LifeRingRegular,
        Boxes,
        ChartPie,
        Store,
        Building,
        User,
        ClipboardList,
        LifeRing,
        SignOutAlt,
        UserCog,
        Plus,
        TrafficLight,
        Buromobelexperte,
        Navbar,
    },
    props: {
        title: String,
        contrast: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            drawerActive: false,
            themeOverrides: {
                common: {
                    boxShadow2:
                        "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                    borderRadius: "4px",
                },
            },
            userOptions: [
                {
                    key: "header",
                    type: "render",
                    render: () => {
                        return h(
                            "div",
                            {
                                class: "flex flex-col items-start py-2 px-4",
                            },
                            [
                                h("div", null, this.userType),
                                h("div", null, this.$page.props.user.name),
                            ]
                        );
                    },
                },
                {
                    label: "Salir",
                    key: "logout",
                    icon: this.renderIcon(LogoutIcon),
                    props: {
                        onClick: () => {
                            this.logout();
                        },
                    },
                },
            ],
        };
    },
    computed: {
        userType() {
            switch (this.$page.props.user.tipo) {
                case "super_admin":
                    return "Super admin";
                case "global_admin":
                    return "Admin";
                case "cliente_admin":
                    return "Cliente";
                case "sucursal_admin":
                    return "Sucursal";
                default:
                    return null;
            }
        },
    },
    methods: {
        openDrawer() {
            this.drawerActive = true;
        },
        renderIcon(icon) {
            return () => {
                return h(NIcon, null, {
                    default: () => h(icon),
                });
            };
        },
        logout() {
            this.$inertia.post(route("logout"));
        },
    },
});
</script>
